// Bot detection and prevention service for APN
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Bot detection patterns and rules
const BOT_DETECTION_RULES = {
  // Suspicious user agents
  suspiciousUserAgents: [
    /bot/i,
    /crawler/i,
    /spider/i,
    /scraper/i,
    /headless/i,
    /phantom/i,
    /selenium/i,
    /puppeteer/i,
    /playwright/i,
    /automated/i
  ],
  
  // Suspicious behavior patterns
  maxActionsPerMinute: 30,
  maxFailedLogins: 5,
  minTimeBetweenActions: 100, // milliseconds
  maxFormSubmissionSpeed: 2000, // minimum time to fill a form
  
  // Required browser features for humans
  requiredFeatures: [
    'localStorage',
    'sessionStorage',
    'indexedDB',
    'WebGL',
    'canvas'
  ]
};

// Track user behavior for bot detection
class BotDetectionTracker {
  constructor() {
    this.userActions = new Map();
    this.suspiciousUsers = new Set();
    this.blockedUsers = new Set();
  }

  // Track user action
  trackAction(userId, actionType, metadata = {}) {
    const now = Date.now();
    
    if (!this.userActions.has(userId)) {
      this.userActions.set(userId, {
        actions: [],
        firstAction: now,
        lastAction: now,
        actionCount: 0,
        failedLogins: 0,
        suspiciousScore: 0
      });
    }

    const userTracker = this.userActions.get(userId);
    userTracker.actions.push({
      type: actionType,
      timestamp: now,
      metadata
    });
    userTracker.lastAction = now;
    userTracker.actionCount++;

    // Check for suspicious patterns
    this.analyzeBehavior(userId);
  }

  // Analyze user behavior for bot patterns
  analyzeBehavior(userId) {
    const userTracker = this.userActions.get(userId);
    if (!userTracker) return;

    let suspiciousScore = 0;
    const recentActions = userTracker.actions.filter(
      action => Date.now() - action.timestamp < 60000 // Last minute
    );

    // Check action frequency
    if (recentActions.length > BOT_DETECTION_RULES.maxActionsPerMinute) {
      suspiciousScore += 30;
    }

    // Check time between actions
    const timeBetweenActions = recentActions.slice(1).map((action, index) => 
      action.timestamp - recentActions[index].timestamp
    );
    
    const avgTimeBetweenActions = timeBetweenActions.reduce((a, b) => a + b, 0) / timeBetweenActions.length;
    if (avgTimeBetweenActions < BOT_DETECTION_RULES.minTimeBetweenActions) {
      suspiciousScore += 25;
    }

    // Check for failed login patterns
    if (userTracker.failedLogins > BOT_DETECTION_RULES.maxFailedLogins) {
      suspiciousScore += 40;
    }

    // Update suspicious score
    userTracker.suspiciousScore = suspiciousScore;

    // Mark as suspicious if score is high
    if (suspiciousScore > 50) {
      this.suspiciousUsers.add(userId);
    }

    // Block if score is very high
    if (suspiciousScore > 80) {
      this.blockedUsers.add(userId);
      this.flagUserAsBot(userId, suspiciousScore);
    }
  }

  // Flag user as potential bot in database
  async flagUserAsBot(userId, suspiciousScore) {
    try {
      await updateDoc(doc(db, 'users', userId), {
        botDetection: {
          flagged: true,
          suspiciousScore,
          flaggedAt: serverTimestamp(),
          reason: 'Automated behavior detected'
        },
        status: 'suspended'
      });
    } catch (error) {
      console.error('Error flagging user as bot:', error);
    }
  }

  // Check if user is blocked
  isUserBlocked(userId) {
    return this.blockedUsers.has(userId);
  }

  // Check if user is suspicious
  isUserSuspicious(userId) {
    return this.suspiciousUsers.has(userId);
  }

  // Get user's suspicious score
  getUserSuspiciousScore(userId) {
    const userTracker = this.userActions.get(userId);
    return userTracker ? userTracker.suspiciousScore : 0;
  }
}

// Global bot detection tracker
const botTracker = new BotDetectionTracker();

// Check browser environment for bot indicators
export const checkBrowserEnvironment = () => {
  const checks = {
    userAgent: navigator.userAgent || '',
    isHeadless: false,
    hasRequiredFeatures: true,
    suspiciousUserAgent: false,
    webDriver: false,
    phantom: false,
    hasMouseMovement: false,
    hasKeyboardInput: false,
    screenResolution: `${window.screen.width || 0}x${window.screen.height || 0}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    language: navigator.language || 'en-US',
    cookiesEnabled: navigator.cookieEnabled || false,
    javaEnabled: navigator.javaEnabled ? navigator.javaEnabled() : false
  };

  // Check for headless browsers
  checks.isHeadless = !!(
    !window.outerHeight ||
    !window.outerWidth ||
    navigator.webdriver ||
    window.navigator.webdriver ||
    window.callPhantom ||
    window._phantom ||
    window.phantom ||
    (window.chrome && window.chrome.runtime && window.chrome.runtime.onConnect === undefined)
  );

  // Check for suspicious user agents
  checks.suspiciousUserAgent = BOT_DETECTION_RULES.suspiciousUserAgents.some(
    pattern => pattern.test(navigator.userAgent || '')
  );

  // Check for WebDriver (consolidated check)
  checks.webDriver = !!(
    navigator.webdriver ||
    window.navigator.webdriver ||
    (document.documentElement && document.documentElement.getAttribute('webdriver')) ||
    (window.chrome && window.chrome.runtime && window.chrome.runtime.onConnect)
  );

  // Check for PhantomJS
  checks.phantom = !!(window.callPhantom || window._phantom);

  // Check for common bot characteristics
  checks.hasRequiredFeatures = !!(
    window.localStorage &&
    window.sessionStorage &&
    window.indexedDB &&
    window.WebSocket &&
    document.createElement
  );

  // Check for suspicious screen resolution (common bot resolutions)
  const suspiciousResolutions = ['1024x768', '800x600', '1366x768'];
  checks.suspiciousResolution = suspiciousResolutions.includes(checks.screenResolution);

  // Check required browser features
  try {
    checks.hasRequiredFeatures = BOT_DETECTION_RULES.requiredFeatures.every(feature => {
      switch (feature) {
        case 'localStorage':
          return typeof Storage !== 'undefined' && !!window.localStorage;
        case 'sessionStorage':
          return typeof Storage !== 'undefined' && !!window.sessionStorage;
        case 'indexedDB':
          return !!window.indexedDB;
        case 'WebGL':
          try {
            return !!(window.WebGLRenderingContext && document.createElement('canvas').getContext('webgl'));
          } catch (e) {
            return false;
          }
        case 'canvas':
          try {
            return !!(document.createElement('canvas').getContext);
          } catch (e) {
            return false;
          }
        default:
          return true;
      }
    });
  } catch (e) {
    checks.hasRequiredFeatures = false;
  }

  return checks;
};

// Perform comprehensive bot detection
export const performBotDetection = async (userId = null) => {
  const browserChecks = checkBrowserEnvironment();
  
  const botScore = calculateBotScore(browserChecks);
  const isBot = botScore > 70;
  
  const result = {
    isBot,
    botScore,
    checks: browserChecks,
    timestamp: new Date().toISOString(),
    userId
  };

  // Track this check if user ID is provided
  if (userId) {
    botTracker.trackAction(userId, 'bot_detection_check', result);
  }

  return result;
};

// Calculate bot score based on checks
const calculateBotScore = (checks) => {
  let score = 0;

  if (checks.isHeadless) score += 40;
  if (checks.suspiciousUserAgent) score += 30;
  if (checks.webDriver) score += 35;
  if (checks.phantom) score += 40;
  if (!checks.hasRequiredFeatures) score += 25;

  return Math.min(score, 100);
};

// Enhanced CAPTCHA verification
export const verifyCaptcha = async (captchaResponse, userId = null) => {
  // This would integrate with a CAPTCHA service like reCAPTCHA
  // For now, we'll simulate the verification
  
  const isValid = captchaResponse && captchaResponse.length > 10;
  
  if (userId) {
    botTracker.trackAction(userId, 'captcha_verification', { 
      success: isValid,
      response: captchaResponse?.substring(0, 10) + '...' 
    });
  }

  return {
    success: isValid,
    score: isValid ? 0.9 : 0.1, // reCAPTCHA-style score
    timestamp: new Date().toISOString()
  };
};

// Track user login attempt
export const trackLoginAttempt = (userId, success, metadata = {}) => {
  botTracker.trackAction(userId, success ? 'login_success' : 'login_failed', metadata);
  
  if (!success) {
    const userTracker = botTracker.userActions.get(userId);
    if (userTracker) {
      userTracker.failedLogins++;
    }
  }
};

// Track form submission timing
export const trackFormSubmission = (userId, formType, timeToComplete) => {
  const isSuspicious = timeToComplete < BOT_DETECTION_RULES.maxFormSubmissionSpeed;
  
  botTracker.trackAction(userId, 'form_submission', {
    formType,
    timeToComplete,
    suspicious: isSuspicious
  });

  return !isSuspicious;
};

// Check if user should be challenged with additional verification
export const shouldChallengeUser = (userId) => {
  return botTracker.isUserSuspicious(userId) || botTracker.getUserSuspiciousScore(userId) > 30;
};

// Block user if they're detected as a bot
export const blockUserIfBot = async (userId) => {
  if (botTracker.isUserBlocked(userId)) {
    throw new Error('User has been blocked due to suspicious activity');
  }
};

// Get user's bot detection status
export const getUserBotStatus = (userId) => {
  return {
    isBlocked: botTracker.isUserBlocked(userId),
    isSuspicious: botTracker.isUserSuspicious(userId),
    suspiciousScore: botTracker.getUserSuspiciousScore(userId),
    shouldChallenge: shouldChallengeUser(userId)
  };
};

// Human interaction tracking
class HumanInteractionTracker {
  constructor() {
    this.interactions = new Map();
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Track mouse movements
    document.addEventListener('mousemove', (e) => {
      this.recordInteraction('mouse_move', { x: e.clientX, y: e.clientY });
    });

    // Track clicks
    document.addEventListener('click', (e) => {
      this.recordInteraction('click', { x: e.clientX, y: e.clientY, target: e.target.tagName });
    });

    // Track keyboard input
    document.addEventListener('keydown', (e) => {
      this.recordInteraction('keydown', { key: e.key, code: e.code });
    });

    // Track scroll behavior
    document.addEventListener('scroll', () => {
      this.recordInteraction('scroll', { scrollY: window.scrollY });
    });

    // Track focus events
    document.addEventListener('focus', (e) => {
      this.recordInteraction('focus', { target: e.target.tagName });
    }, true);
  }

  recordInteraction(type, data) {
    const userId = this.getCurrentUserId();
    if (!userId) return;

    if (!this.interactions.has(userId)) {
      this.interactions.set(userId, []);
    }

    const userInteractions = this.interactions.get(userId);
    userInteractions.push({
      type,
      data,
      timestamp: Date.now()
    });

    // Keep only last 100 interactions per user
    if (userInteractions.length > 100) {
      userInteractions.shift();
    }
  }

  getCurrentUserId() {
    // This would be set by the auth context
    return window.currentUserId || null;
  }

  getHumanScore(userId) {
    const interactions = this.interactions.get(userId) || [];
    if (interactions.length === 0) return 0;

    let humanScore = 0;
    const now = Date.now();
    const recentInteractions = interactions.filter(i => now - i.timestamp < 300000); // Last 5 minutes

    // Score based on interaction variety
    const types = new Set(recentInteractions.map(i => i.type));
    humanScore += types.size * 10; // 10 points per interaction type

    // Score based on mouse movement patterns
    const mouseMovements = recentInteractions.filter(i => i.type === 'mouse_move');
    if (mouseMovements.length > 10) {
      humanScore += 20; // Natural mouse movement
    }

    // Score based on typing patterns
    const keystrokes = recentInteractions.filter(i => i.type === 'keydown');
    if (keystrokes.length > 5) {
      humanScore += 15; // Human typing detected
    }

    // Score based on scroll behavior
    const scrolls = recentInteractions.filter(i => i.type === 'scroll');
    if (scrolls.length > 3) {
      humanScore += 10; // Natural scrolling
    }

    return Math.min(humanScore, 100); // Cap at 100
  }

  isHumanBehavior(userId) {
    return this.getHumanScore(userId) > 30;
  }
}

// Global human interaction tracker
const humanTracker = new HumanInteractionTracker();

// Export human interaction functions
export const getHumanInteractionScore = (userId) => {
  return humanTracker.getHumanScore(userId);
};

export const isHumanBehavior = (userId) => {
  return humanTracker.isHumanBehavior(userId);
};

export const setCurrentUserId = (userId) => {
  window.currentUserId = userId;
};

export default {
  performBotDetection,
  checkBrowserEnvironment,
  verifyCaptcha,
  trackLoginAttempt,
  trackFormSubmission,
  shouldChallengeUser,
  blockUserIfBot,
  getUserBotStatus
};
